import React, { useEffect, useState } from 'react';

import { ACTIVITY_TYPE_TO_ICON } from '@/administration/pages/Course/MainLayoutView/types';
import { Icon } from '@/ui/Icon';
import { Activity, ActivityContent, ActivityType } from '@/features/course/admin/types/activity';
import { Skeleton } from '@/ui/Skeleton';
import { makeNamespacedComponent } from '@/ui/utils';
import { IconPropType } from '@/types/icon';
import { ActivityId } from '@/administration/pages/Course/store/types';
import {
  StyledCollapseIcon,
  IconWrapStyled,
  TitleWrap,
  TitleText,
  RootStyled,
} from '@/administration/pages/Course/MainLayoutView/atoms/ViewItemV2/styles';

export type ViewItemV2Props = {
  id: string;
  content: ActivityContent;
  type: ActivityType;
  active?: boolean;
  onSelect: (id: ActivityId) => void;
  completed?: boolean;
  isChild?: boolean;
  children?: Activity[];
  activeContentId?: string;
};

const MAX_TITLE_LENGTH = 55;
const MAX_CHILD_TITLE_LENGTH = 40;

const getShortenTitle = (title?: string, isChild?: boolean, parentLimit?: number, childLimit?: number): string => {
  if (!title) return '';

  const resultParentLimit = parentLimit || MAX_TITLE_LENGTH;
  const resultChildLimit = childLimit || MAX_CHILD_TITLE_LENGTH;

  const limit = isChild ? resultChildLimit : resultParentLimit;

  if (title?.length < limit) {
    return title;
  } else {
    return `${title?.slice(0, limit)}...`;
  }
};

const ViewItemV2Base = ({
  id,
  content,
  active,
  type,
  onSelect,
  completed,
  isChild,
  children,
  activeContentId,
}: ViewItemV2Props): JSX.Element => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const drawerItemType = completed ? 'completed' : type;
  const isModule = type === ActivityType.Module;

  const handleCollapseClick = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleItemClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (isModule) {
      handleCollapseClick();
    } else {
      onSelect(id as ActivityId);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();

      if (isModule) {
        handleCollapseClick();
      } else {
        onSelect(id as ActivityId);
      }
    }
  };

  const keyboardA11yProps = {
    role: 'button',
    tabIndex: 0,
    onKeyDown: handleKeyDown,
    'aria-expanded': isModule && !!children?.length ? !isCollapsed : undefined,
    'aria-current': active ? ('true' as const) : undefined,
  };

  useEffect(() => {
    if (type === ActivityType.Module && activeContentId) {
      if (children?.some((child) => child.id === activeContentId)) {
        setIsCollapsed(false);
      } else {
        setIsCollapsed(true);
      }
    }
  }, [activeContentId, children]);

  return (
    <>
      <RootStyled data-item-id={id} onClick={handleItemClick} active={!!active} isChild={isChild} {...keyboardA11yProps}>
        <TitleWrap>
          <IconWrapStyled type={drawerItemType}>
            <Icon size="xs" icon={ACTIVITY_TYPE_TO_ICON[drawerItemType] as IconPropType} />
          </IconWrapStyled>
          <TitleText title={content?.title || ''}>{getShortenTitle(content?.title || '', isChild)}</TitleText>
        </TitleWrap>
        {!!children?.length && (
          <StyledCollapseIcon icon={isCollapsed ? 'angle-down' : 'angle-up'} onClick={handleCollapseClick} />
        )}
      </RootStyled>
      {!isCollapsed &&
        children?.map((child) => {
          return (
            <ViewItemV2Base {...child} type={child?.type as unknown as ActivityType} key={child.id} isChild onSelect={onSelect} />
          );
        })}
    </>
  );
};

const LoadingState = (): JSX.Element => <Skeleton width={312} height={36} />;

export const ViewItemV2 = makeNamespacedComponent(ViewItemV2Base, {
  LoadingState,
});
